// Font picker — autocomplete input + dropdown of curated Google fonts.
//
// 1-to-1 port of the live editor's font picker (snapshot
// docs/olm-editor-LIVE-snapshot.html: FONTS list L2619-2661, showFontPicker /
// filterFonts / renderFontOptions / selectFont / preloadAllFonts L4785-4874).
//
// Public contract (other agents wire to this in parallel):
//   export function mountFontPicker(host, opts?): void
// The published contract uses `onChange`; the TASK prose uses `onSelect`. To be
// safe regardless of which the properties agent passes, we accept BOTH and
// prefer `onChange`.
//
// Self-contained: only consumes `el` from ui/dom. The font is applied by the
// caller's callback (properties agent uses applyFont from widgets/text); this
// module's job is purely the UI + lazy Google-font loading.

import { el } from '../ui/dom'

/** Curated font list — copied VERBATIM from the live editor (snapshot L2619-2661). */
const FONTS: string[] = [
  // Sans-serif — clean & modern
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Raleway', 'Oswald',
  'Nunito', 'Nunito Sans', 'Source Sans 3', 'PT Sans', 'Ubuntu', 'Roboto Condensed',
  'Noto Sans', 'Rubik', 'Work Sans', 'Fira Sans', 'Quicksand', 'Barlow', 'Cabin',
  'DM Sans', 'Space Grotesk', 'Plus Jakarta Sans', 'Outfit', 'Sora', 'Manrope', 'Lexend',
  'Archivo', 'Josefin Sans', 'Karla', 'Mukta', 'Exo 2', 'Titillium Web', 'Dosis',
  'Catamaran', 'Kanit', 'Mulish', 'Hind', 'Asap', 'Signika', 'Sarabun',
  'Noto Sans JP', 'Noto Sans KR', 'Noto Sans SC',
  'Figtree', 'Geist', 'Albert Sans', 'Red Hat Display', 'Urbanist', 'Schibsted Grotesk',
  'Bricolage Grotesque', 'Instrument Sans', 'Onest',
  // Sans-serif — narrow / condensed
  'Barlow Condensed', 'PT Sans Narrow', 'Saira Condensed',
  'Yanone Kaffeesatz', 'Fjalla One', 'Pathway Gothic One',
  // Serif — elegant & traditional
  'Playfair Display', 'Merriweather', 'Libre Baskerville', 'Lora', 'PT Serif',
  'Source Serif 4', 'Noto Serif', 'EB Garamond', 'Crimson Text', 'Bitter',
  'Cormorant Garamond', 'Libre Caslon Text', 'Spectral', 'DM Serif Display',
  'DM Serif Text', 'Fraunces', 'Young Serif', 'Instrument Serif',
  'Bodoni Moda', 'Cormorant', 'IBM Plex Serif', 'Zilla Slab',
  // Slab serif
  'Roboto Slab', 'Arvo', 'Crete Round', 'Slabo 27px', 'Rokkitt', 'Josefin Slab',
  'Alfa Slab One',
  // Display / heading
  'Anton', 'Bebas Neue', 'Righteous', 'Russo One', 'Bungee', 'Fredoka',
  'Lilita One', 'Abril Fatface', 'Passion One', 'Rampart One', 'Black Ops One',
  'Secular One', 'Fugaz One', 'Staatliches', 'Teko', 'Chakra Petch',
  'Saira', 'Big Shoulders Display', 'League Spartan', 'Syne',
  // Handwriting / script
  'Dancing Script', 'Pacifico', 'Lobster', 'Caveat', 'Satisfy', 'Great Vibes',
  'Sacramento', 'Permanent Marker', 'Indie Flower', 'Amatic SC', 'Kalam',
  'Shadows Into Light', 'Patrick Hand', 'Gloria Hallelujah', 'Architects Daughter',
  'Courgette', 'Cookie', 'Kaushan Script', 'Yellowtail', 'Leckerli One',
  'Homemade Apple', 'Rock Salt', 'Just Another Hand', 'Handlee',
  // Monospace
  'Roboto Mono', 'Fira Code', 'Source Code Pro', 'JetBrains Mono', 'IBM Plex Mono',
  'Space Mono', 'Ubuntu Mono', 'Inconsolata', 'Courier Prime', 'DM Mono',
  // Decorative / fun
  'Comfortaa', 'Baloo 2', 'Boogaloo', 'Chewy', 'Bangers',
  'Press Start 2P', 'Silkscreen', 'Orbitron', 'Audiowide',
  // System fallbacks (always available)
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana',
]

/** System fonts that are always available and must NOT be lazy-loaded from Google. */
const SYSTEM_FONTS = new Set([
  'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana',
])

// ---- Module-level lazy-load state (deduped across every picker instance) ----

/** Families whose Google Fonts <link> has been injected (or are system fonts). */
const loadedFonts = new Set<string>(['Inter', ...SYSTEM_FONTS])
/** Whether the batched preload of the full FONTS list has already run. */
let fontsPreloaded = false

/** Inject a single Google Fonts stylesheet link (no-op if already loaded/system). */
function injectFontLink(family: string): void {
  if (loadedFonts.has(family) || SYSTEM_FONTS.has(family)) return
  loadedFonts.add(family)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href =
    `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@400;700&display=swap`
  document.head.appendChild(link)
}

/**
 * Batch-load every Google font in the curated list (URL length limit ~8KB, so
 * we chunk at 40 families per <link>). Mirrors live preloadAllFonts (L4790).
 * Runs at most once per page session.
 */
function preloadAllFonts(): void {
  if (fontsPreloaded) return
  fontsPreloaded = true
  const googleFonts = FONTS.filter((f) => !SYSTEM_FONTS.has(f))
  const batchSize = 40
  for (let i = 0; i < googleFonts.length; i += batchSize) {
    const batch = googleFonts.slice(i, i + batchSize)
    const families = batch
      .map((f) => `family=${f.replace(/ /g, '+')}:wght@400;700`)
      .join('&')
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`
    document.head.appendChild(link)
  }
  FONTS.forEach((f) => loadedFonts.add(f))
}

export interface FontPickerOptions {
  /** Initial font family shown in the input. Defaults to 'Inter'. */
  value?: string
  /** Preferred callback (published contract name). Fired with the chosen family. */
  onChange?: (family: string) => void
  /** Alias accepted for compatibility with the TASK prose; used if onChange absent. */
  onSelect?: (family: string) => void
}

/**
 * Mount an autocomplete font picker into `host`.
 *
 * Renders a `.prop-input` text field with a filtering `.dropdown` of
 * `.dropdown-item` rows (each previewed in its own font-family). On select it
 * lazy-loads the chosen Google font and, once glyphs are available, invokes
 * `opts.onChange` (preferred) or `opts.onSelect`.
 */
export function mountFontPicker(host: HTMLElement, opts: FontPickerOptions = {}): void {
  const emit = (family: string): void => {
    if (opts.onChange) opts.onChange(family)
    else if (opts.onSelect) opts.onSelect(family)
  }

  // position:relative wrapper so the absolutely-positioned dropdown anchors here.
  const wrap = el('div', 'relative')

  const input = el('input', 'prop-input') as HTMLInputElement
  input.type = 'text'
  input.placeholder = 'Font family'
  input.value = opts.value || 'Inter'

  // Live #fontPicker inline style (snapshot L1239): anchored under the input,
  // scrollable. `.dropdown.hidden` matches the shared dropdown look + tests.
  const menu = el('div', 'dropdown hidden')
  menu.style.top = '34px'
  menu.style.left = '0'
  menu.style.right = '0'
  menu.style.maxHeight = '200px'
  menu.style.overflowY = 'auto'

  const renderOptions = (query: string): void => {
    const q = query.toLowerCase()
    const filtered = FONTS.filter((f) => f.toLowerCase().includes(q))
    menu.replaceChildren()
    for (const f of filtered) {
      const row = el('div', 'dropdown-item', f)
      row.style.fontFamily = `'${f}', sans-serif`
      row.style.lineHeight = '1.6'
      // mousedown (not click) so the input doesn't blur-close before we handle it.
      row.addEventListener('mousedown', (e) => {
        e.preventDefault()
        selectFont(f)
      })
      menu.appendChild(row)
    }
  }

  const selectFont = (family: string): void => {
    menu.classList.add('hidden')
    input.value = family
    injectFontLink(family)

    // Apply only once the glyphs are available (mirrors live applyWhenReady →
    // applyFont) so the caller's canvas re-render uses the right metrics.
    const ready = () => emit(family)
    if (typeof document.fonts !== 'undefined') {
      if (document.fonts.check(`16px "${family}"`)) ready()
      else document.fonts.ready.then(ready)
    } else {
      setTimeout(ready, 500)
    }
  }

  input.addEventListener('focus', () => {
    preloadAllFonts()
    menu.classList.remove('hidden')
    renderOptions('')
  })
  input.addEventListener('input', () => renderOptions(input.value))

  // Close when clicking outside the wrapper (mousedown fires before blur).
  document.addEventListener('mousedown', (e) => {
    if (!wrap.contains(e.target as Node)) menu.classList.add('hidden')
  })

  wrap.append(input, menu)
  host.appendChild(wrap)
}
